import React, { useRef, useEffect } from 'react';
import { Appointment } from './AppointmentCard';
import AppointmentCard from './AppointmentCard';
import './SchedulingCalendar.css';

interface Professional {
    id: number;
    name: string;
    photo?: string;
    bio?: string;
    agendas?: any[];
}

interface DayViewProps {
    date: Date;
    appointments: Appointment[];
    professionals: Professional[];
    onTimeSlotClick: (time: Date, professionalId: number) => void;
    onAppointmentClick: (appointment: Appointment) => void;
    holidays: any[];
    // deprecated: interval, startHour, endHour (now derived per professional)
    interval?: number;
    startHour?: number;
    endHour?: number;
}

const DayView: React.FC<DayViewProps> = ({
    date,
    appointments,
    professionals,
    holidays,
    onTimeSlotClick,
    onAppointmentClick
}) => {

    const slotHeight = 60; // Fixed height per slot (row)

    // Current Time Indicator Logic (Global)
    const [currentTime, setCurrentTime] = React.useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000); // Update every minute
        return () => clearInterval(timer);
    }, []);

    // Helper to get configuration for a specific professional
    const getProfessionalConfig = (prof: Professional) => {
        // Default
        let interval = 30;
        let startH = 8;
        let endH = 19;
        let currentAgenda: any = null;

        if (prof.agendas && prof.agendas.length > 0) {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            const currentISO = `${y}-${m}-${d}`;

            currentAgenda = prof.agendas.find((a: any) => {
                // Check dates (open ended or specific)
                const startValid = !a.start_date || a.start_date <= currentISO;
                const endValid = !a.end_date || a.end_date >= currentISO;
                return startValid && endValid;
            });

            if (currentAgenda) {
                if (currentAgenda.slot_duration) {
                    const parts = currentAgenda.slot_duration.split(':');
                    if (parts.length >= 2) {
                        const h = parseInt(parts[0], 10);
                        const min = parseInt(parts[1], 10);
                        const total = h * 60 + min;
                        if (total > 0) interval = total;
                    }
                }
                if (currentAgenda.start_time) {
                    const [h] = currentAgenda.start_time.split(':').map(Number);
                    if (!isNaN(h)) startH = h;
                }
                if (currentAgenda.end_time) {
                    const [h, m] = currentAgenda.end_time.split(':').map(Number);
                    let finalH = h;
                    if (m > 0) finalH = h + 1;
                    startH = startH < finalH ? startH : 8; // Safety
                    endH = finalH > startH ? finalH : 19;
                }
            }
        }
        return { interval, startH, endH, currentAgenda };
    };

    const renderProfessionalTable = (prof: Professional) => {
        const { interval, startH, endH, currentAgenda } = getProfessionalConfig(prof);

        // Generate slots for this professional
        const slots: Date[] = [];
        let current = new Date(date);
        current.setHours(startH, 0, 0, 0);
        const endTime = new Date(date);
        endTime.setHours(endH, 0, 0, 0);

        while (current < endTime) {
            slots.push(new Date(current));
            current.setMinutes(current.getMinutes() + interval);
        }

        const getAppointmentsForSlot = (slot: Date, professionalId: number) => {
            const slotStartMs = slot.getTime();
            const slotEndMs = slotStartMs + interval * 60 * 1000;

            return appointments.filter(a => {
                if (a.professional !== professionalId) return false;
                const startMs = new Date(a.start_time).getTime();
                return startMs >= slotStartMs && startMs < slotEndMs;
            });
        };

        const checkSlotBlocked = (slot: Date) => {
            if (!currentAgenda) return { blocked: true, reason: 'Indisponível' };

            // 1. Weekday Check
            const weekDaysMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const dayName = weekDaysMap[slot.getDay()];
            if (currentAgenda[dayName] === false) {
                return { blocked: true, reason: 'Indisponível' };
            }

            // 2. Holiday Check
            // Reset time to check date equality
            const y = slot.getFullYear();
            const m = String(slot.getMonth() + 1).padStart(2, '0');
            const d = String(slot.getDate()).padStart(2, '0');
            const slotDateISO = `${y}-${m}-${d}`;

            const isHoliday = holidays.find(h => h.date === slotDateISO);
            if (isHoliday && !currentAgenda.allow_holidays) {
                return { blocked: true, reason: 'Indisponível' };
            }

            // 3. Lunch Check
            if (currentAgenda.lunch_start_time && currentAgenda.lunch_end_time) {
                const slotTimeVal = slot.getHours() * 60 + slot.getMinutes();
                const [lsH, lsM] = currentAgenda.lunch_start_time.split(':').map(Number);
                const [leH, leM] = currentAgenda.lunch_end_time.split(':').map(Number);
                const lunchStartVal = lsH * 60 + lsM;
                const lunchEndVal = leH * 60 + leM;

                // Simple overlap check: SlotStart < LunchEnd AND SlotEnd > LunchStart
                // SlotEnd is SlotStart + interval
                const slotEndVal = slotTimeVal + interval;

                if (slotTimeVal < lunchEndVal && slotEndVal > lunchStartVal) {
                    return { blocked: true, reason: 'Indisponível' };
                }
            }

            return { blocked: false, reason: '' };
        };

        const getAppointmentStyle = (appointment: Appointment, slotTime: Date): React.CSSProperties => {
            const start = new Date(appointment.start_time);
            const end = new Date(appointment.end_time);

            const startH = start.getHours();
            const startM = start.getMinutes();
            const endH = end.getHours();
            const endM = end.getMinutes();

            const durationMinutes = (endH * 60 + endM) - (startH * 60 + startM);
            const slotStartMinutes = slotTime.getHours() * 60 + slotTime.getMinutes();
            const appStartMinutes = startH * 60 + startM;
            const minutesFromSlotStart = Math.max(0, appStartMinutes - slotStartMinutes);
            const pxPerMinute = slotHeight / interval;

            const top = minutesFromSlotStart * pxPerMinute;
            const height = durationMinutes * pxPerMinute;

            return {
                top: `${top}px`,
                height: `${height}px`,
                position: 'absolute',
                width: '100%',
                left: '0',
                zIndex: 10
            };
        };

        // Calculate Current Time Indicator Position for this specific table configuration
        const calcCurrentTimeTop = () => {
            const currentH = currentTime.getHours();
            const currentM = currentTime.getMinutes();
            const totalMinutes = currentH * 60 + currentM;
            const startMinutes = startH * 60;
            const endMinutes = endH * 60;

            if (totalMinutes < startMinutes || totalMinutes > endMinutes) return null;

            const minutesFromStart = totalMinutes - startMinutes;
            const minutesPerSlot = interval; // assumption
            const pxPerMinute = slotHeight / interval;

            return minutesFromStart * pxPerMinute;
        };

        const currentTimeTop = calcCurrentTimeTop();

        return (
            <div key={prof.id} className="d-flex flex-column border rounded shadow-sm bg-white day-view-prof-col" style={{ minWidth: '300px', flex: 1 }}>
                {/* Header */}
                <div className="day-view-prof-header p-3 border-bottom d-flex align-items-center justify-content-center gap-2 bg-light rounded-top" style={{ height: '80px' }}>
                    <div className="professional-avatar" style={{ width: 40, height: 40 }}>
                        {prof.photo ? (
                            <img src={prof.photo} alt={prof.name} className="w-100 h-100 rounded-circle active-avatar" />
                        ) : (
                            prof.name.charAt(0).toUpperCase()
                        )}
                    </div>
                    <div className="d-flex flex-column text-start overflow-hidden">
                        <span className="fw-bold text-dark text-truncate" style={{ maxWidth: '150px' }}>{prof.name}</span>
                        <small className="text-muted">{prof.bio || 'Profissional'}</small>
                    </div>
                </div>

                {/* Table Body Container */}
                <div className="position-relative flex-grow-1" style={{ overflowY: 'auto' }}>
                    {/* Current Time Line */}
                    {currentTimeTop !== null && (
                        <div
                            className="current-time-line"
                            style={{
                                top: `${currentTimeTop}px`,
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                width: '100%',
                                zIndex: 60,
                                borderTop: '2px solid red',
                                pointerEvents: 'none'
                            }}
                        >
                            <div className="current-time-badge" style={{
                                position: 'absolute',
                                left: 0,
                                top: '-10px',
                                background: 'red',
                                color: 'white',
                                fontSize: '0.7rem',
                                padding: '2px 4px',
                                borderRadius: '0 4px 4px 0'
                            }}>
                                {currentTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                        </div>
                    )}

                    <table className="table table-bordered mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                        <tbody>
                            {slots.map((slot, index) => {
                                const { blocked, reason } = checkSlotBlocked(slot);
                                return (
                                    <tr key={index}>
                                        {/* Time Column (included in every table now) */}
                                        <td className="bg-light text-center align-top p-0 border-end"
                                            style={{
                                                height: `${slotHeight}px`,
                                                width: '60px',
                                                verticalAlign: 'top',
                                                borderBottom: '1px solid #eaecf0'
                                            }}>
                                            <span className="d-block text-muted small mt-1" style={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                                {slot.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </td>

                                        {/* Appointment Slot */}
                                        <td
                                            className={`p-0 position-relative ${blocked ? 'bg-light' : 'time-slot-hover'}`}
                                            style={{
                                                height: `${slotHeight}px`,
                                                verticalAlign: 'top',
                                                borderBottom: '1px solid #eaecf0',
                                                backgroundColor: blocked ? '#f3f4f6' : '#fafafa',
                                                cursor: blocked ? 'not-allowed' : 'pointer'
                                            }}
                                            onClick={() => !blocked && onTimeSlotClick(slot, prof.id)}>

                                            {blocked && (
                                                <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted small user-select-none">
                                                    {reason}
                                                </div>
                                            )}

                                            {!blocked && getAppointmentsForSlot(slot, prof.id).map(appointment => (
                                                <AppointmentCard
                                                    key={appointment.id}
                                                    appointment={appointment}
                                                    onClick={onAppointmentClick}
                                                    style={getAppointmentStyle(appointment, slot)}
                                                    showProfessional={false} // Already in own column
                                                />
                                            ))}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="calendar-container-scroll bg-light p-2 p-md-3" style={{ height: '100%', overflow: 'hidden' }}>
            <div className="day-view-scroll d-flex flex-nowrap gap-2 gap-md-3" style={{ height: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
                {professionals.map(prof => renderProfessionalTable(prof))}
            </div>
        </div>
    );
};

export default DayView;
